import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Toast from '../ErrorBoundry/Toast';
import { useAppDispatch, useAppSelector } from '../../ReduxTool/store/hooks';
import SearchBar from './SearchBar';
import { ArrowRightIcon, FilterIcon, IconDashboard, RingLoaderIcon, SortIcon } from '../../assests/icons/Icons';
import { IconLeftArrow, IconRighttArrow } from '../../assests/icons/DrawerIcons';
import { debounce, formatDate2 } from '../../Utils/commonFunctions';
import { NewModal } from '../New/Modal/NewModal';
import TicketUserDetails, { TicketUserDetailsHandle } from './TicketUserDetails';
import { MessageDotIcon } from '../../assests/icons/EPCIcons/Icons';
import { SupportTicketParams } from '../../ReduxTool/Slice/CommonReducers/types';
import { getSupportById, getSupportTickets, submitSupportFeedback } from '../../ReduxTool/Slice/CommonReducers/CommonActions';
import { useNavigate } from 'react-router-dom';
import { setModalHeaderFooter } from '../../ReduxTool/Slice/CommonReducers/CommonReducers';

const useOutside = (ref: any, cb: () => void) => {
    useEffect(() => {
        const fn = (e: MouseEvent) =>
            ref.current && !ref.current.contains(e.target) && cb();
        document.addEventListener("mousedown", fn);
        return () => document.removeEventListener("mousedown", fn);
    }, [cb, ref]);
};

// Status map: label → API integer value
const STATUS_MAP: Record<string, number> = { "Received": 1, "In Review": 2, "Resolved": 3, };

const STATUS_DISPLAY: Record<number, string> = { 1: "Received", 2: "In Review", 3: "Resolved", };

interface filter {
    category: string;
    status: string;   // "All" | "Received" | "In Review" | "Resolved"
}

type itemPerPageTY = 10 | 50 | 100 | 200;
type ProfileModalType = "viewticket" | null;
interface ProfileModalOptions { viewticket: boolean; }

function SupportTrack() {
    const [selected, setSelected] = useState<string | null>(null);
    const [showFilter, setShowFilter] = useState(false);
    const [showSort, setShowSort] = useState(false);
    const [showItemPerPage, setShowItemPerPage] = useState(false);
    const { user } = useAppSelector(state => state.auth)
    const userid = user?.userid ?? "";
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // Pending filter state (only applied on "Apply" click)
    const [pendingFilters, setPendingFilters] = useState<filter>({ category: "All", status: "All" });
    // Applied filter state (used in API calls)
    const [appliedFilters, setAppliedFilters] = useState<filter>({ category: "All", status: "All" });

    const [tableData, setTableData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState<string>("");
    const [sortDir, setSortDir] = useState<string>("");
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalResults, setTotalResults] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState<itemPerPageTY>(10);
    const [profileModals, setProfileModals] = useState<ProfileModalOptions>({ viewticket: false });
    const [viewTicket, setViewTicket] = useState<any>(null);
    const [openSections, setOpenSections] = useState({ category: true, status: true });

    const filterRef = useRef<HTMLDivElement>(null);
    const sortRef = useRef<HTMLDivElement>(null);
    const itemsperpageRef = useRef<HTMLDivElement>(null);

    const ticketRef = useRef<TicketUserDetailsHandle>(null);
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
    const [feedbackSubmitError, setFeedbackSubmitError] = useState("");
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

    const [ticketDetail, setTicketDetail] = useState<any>(null);
    const [ticketDetailLoading, setTicketDetailLoading] = useState(false);

    useOutside(filterRef, () => setShowFilter(false));
    useOutside(sortRef, () => setShowSort(false));
    useOutside(itemsperpageRef, () => setShowItemPerPage(false));

    const toggleSection = (section: 'category' | 'status') => {
        setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Core fetch function — always uses appliedFilters
    const fetchTickets = useCallback(async (page = 0, filters: filter = appliedFilters, perPage: itemPerPageTY = itemsPerPage, currentSortBy = sortBy, currentSortDir = sortDir, ticketcode = "") => {
        setTableLoading(true);
        try {
            const params: SupportTicketParams = { userid, start: page, pageSize: perPage, };
            // Only add optional params if they have real values
            if (filters.category !== "All") params.subject = filters.category;
            if (filters.status !== "All") params.status = STATUS_MAP[filters.status];
            if (currentSortBy) params.sortBy = currentSortBy;
            if (currentSortDir) params.sortDir = currentSortDir;
            if (ticketcode.trim()) {
                const value = ticketcode.trim();
                // Ticket codes follow pattern SUP-XXXXX — detect by prefix
                if (value.toUpperCase().startsWith("SUP-") || /^\d+$/.test(value)) {
                    params.ticketcode = value;
                } else {
                    params.subject = value;   // treat as subject search
                }
            }

            const result = await getSupportTickets(params);
            if (result) {
                setTableData(result.records ?? []);
                setTotalResults(result.totalRecords ?? 0);
                // calculate total pages based on totalRecords and pageSize
                setTotalPages(Math.ceil((result.totalRecords ?? 0) / perPage));
                setCurrentPage(page);
            }
        } catch (err) {
            Toast({ messageText: "Failed to load tickets", messageType: "E", toastId: "ticketErr", autoClose: 2000 });
        } finally {
            setTableLoading(false);
        }
    }, [appliedFilters, itemsPerPage, sortBy, sortDir]);

    // Initial load — no params, just start=0&pageSize=10
    useEffect(() => {
        const initialFetch = async () => {
            setLoading(true);
            await fetchTickets(0, { category: "All", status: "All" }, 10, "", "");
            setLoading(false);
        };
        initialFetch();
    }, []);

    // Apply filters — only triggered by "Apply" button click
    const handleApplyFilters = () => {
        setAppliedFilters(pendingFilters);  // commit pending → applied
        setCurrentPage(0);
        setShowFilter(false);
        fetchTickets(0, pendingFilters, itemsPerPage, sortBy, sortDir);
    };

    // Sort — applied immediately on selection
    const handleSortSelect = (sortOption: string) => {
        let newSortBy = "";
        let newSortDir = "";

        switch (sortOption) {
            case "A to Z":
                newSortBy = "";
                newSortDir = "asc";
                break;
            case "Z to A":
                newSortBy = "";
                newSortDir = "desc";
                break;
            case "Newest":
                newSortBy = "createdat";
                newSortDir = "";
                break;
        }

        setSortBy(newSortBy);
        setSortDir(newSortDir);
        setShowSort(false);
        setCurrentPage(0);
        fetchTickets(0, appliedFilters, itemsPerPage, newSortBy, newSortDir);
    };

    // Pagination
    const handlePageChange = (newPage: number) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            fetchTickets(newPage, appliedFilters, itemsPerPage, sortBy, sortDir);
        }
    };

    // Items per page
    const handleItemsPerPageChange = (val: itemPerPageTY) => {
        setItemsPerPage(val);
        setCurrentPage(0);
        setShowItemPerPage(false);
        fetchTickets(0, appliedFilters, val, sortBy, sortDir);
    };

    // Search by ticketcode (debounced)
    const debouncedSearch = useMemo(() => debounce((value: string) => {
        fetchTickets(0, appliedFilters, itemsPerPage, sortBy, sortDir, value);
    }, 500), [appliedFilters, itemsPerPage, sortBy, sortDir]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearch(value);
        if (value.length > 2 || value === "") {
            debouncedSearch(value);
        }
    };

    // Reset all filters
    const resetAll = () => {
        const reset: filter = { category: "All", status: "All" };
        setPendingFilters(reset);
        setAppliedFilters(reset);
        setSortBy("");
        setSortDir("");
        setSearch("");
        setCurrentPage(0);
        fetchTickets(0, reset, itemsPerPage, "", "");
    };

    const openManageTicketModal = async (modalTy: ProfileModalType, item?: any) => {
        if (modalTy === "viewticket") {
            setProfileModals({ viewticket: true });
            setViewTicket(item);
            setSelected(item?.supportid);
            setFeedbackSubmitted(false);   // reset on open
            setFeedbackSubmitError("")
            setTicketDetail(null);

            setTicketDetailLoading(true);
            const detail = await getSupportById(item?.supportid);
            setTicketDetail(detail);
            setTicketDetailLoading(false);
        } else {
            setProfileModals({ viewticket: false });
            setViewTicket(null);
            setSelected(null);
            setFeedbackSubmitted(false)
            setFeedbackSubmitError("")
            setTicketDetail(null);
        }
    };

    const getTableRowData = (item: any) => ({
        ticketId: item.ticketcode || "--",
        userName: `${item.fname ?? ""} ${item.lname ?? ""}`.trim() || "--",
        category: item.subject || "--",
        createdTime: formatDate2(item.createdat),
        updatedTime: formatDate2(item.updatedat),
        status: typeof item.status === "number"
            ? STATUS_DISPLAY[item.status] ?? "In Review"
            : item.status ?? "In Review",
        id: item.supportid,
    });

    const startItem = currentPage * itemsPerPage + 1;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalResults);

    if (loading) {
        return <div className="flex justify-center h-[63vh]"><RingLoaderIcon size={4} /></div>;
    }

    const handleSubmitFeedback = async () => {
        const result = ticketRef.current?.getSubmitBody();
        if (!result?.valid) return;  // validation failed — error shown inside child

        setFeedbackSubmitting(true);
        setFeedbackSubmitError("");
        try {
            const res = await submitSupportFeedback(viewTicket?.supportid, result.body!);
            if (res === "200") {
                setFeedbackSubmitted(true);
            } else {
                setFeedbackSubmitError("Something went wrong. Please try again.");
            }
        } catch {
            setFeedbackSubmitError("Something went wrong. Please try again.");
        }
        setFeedbackSubmitting(false);
    };

    const isSuccessState = feedbackSubmitted || ticketDetail?.rating != null;
    const lastMsg = ticketDetail?.conversation?.messages?.slice(-1)[0] ?? null;
    const showFooter = lastMsg?.senderType === 2        // last message is admin
        && ticketDetail?.rating == null                   // not already rated
        && !feedbackSubmitted;

    const handleGoToDashboard = () => {
        dispatch(setModalHeaderFooter({ title: '', btnTxt: '', secondaryBtnTxt: '', modalid: 'manageticket', isheader: false }));
        openManageTicketModal(null);
        navigate("/");
    };

    return (
        <>
            {profileModals.viewticket && (
                <NewModal isAbleCLick scrollhide height='fit-content' maxheight='63vh' modalSize="lg" modelbtn={isSuccessState  ? "Go to Dashboard" : "Submit Feedback"} btnName='' onClick={isSuccessState ? handleGoToDashboard : handleSubmitFeedback} isLoading={feedbackSubmitting} isFooterRequired={showFooter || isSuccessState} setIsCLose={() => openManageTicketModal(null)} name={"manageticket"} title='Manage Ticket' primarylefticon={isSuccessState  ? <IconDashboard /> : undefined} children={<TicketUserDetails data={viewTicket} ref={ticketRef} submitError={feedbackSubmitError} submitted={feedbackSubmitted} ticketDetail={ticketDetail} ticketDetailLoading={ticketDetailLoading} />} />
            )}

            <div className="flex h-[68vh] flex-col scrollbar-hide">
                {/* SEARCH + FILTER + SORT */}
                <div className="flex items-center justify-between gap-4">
                    <div className="searchbar-padding w-[25rem] mt-3 pl-1">
                        <SearchBar value={search} width={true} onChange={handleSearch} placeholder="Search by ticket id or category." />
                    </div>

                    <div className="relative flex items-center gap-4 text-sm text-[#111827]">
                        {/* FILTER */}
                        <div ref={filterRef} className="relative mr-1">
                            <button onClick={() => setShowFilter(p => !p)} className="flex text-nowrap">
                                <span className="self-center pr-1"><FilterIcon /></span>Filter
                                <span className="self-center pl-1"><ArrowRightIcon className="rotate-90 ml-1" /></span>
                            </button>

                            {showFilter && (
                                <div className="absolute right-0 top-8 z-50 w-[310px] rounded-xl border border-gray-200 bg-white shadow-xl p-4 space-y-4">
                                    {/* CATEGORY */}
                                    <div>
                                        <button onClick={() => toggleSection('category')} className="flex w-full items-center justify-between py-2 text-sm font-semibold">
                                            Category <ArrowRightIcon className={`transition-transform duration-200 ${openSections.category ? "rotate-90" : "rotate-0"}`} />
                                        </button>
                                        {openSections.category && (
                                            <div className="space-y-1 text-sm transition-all">
                                                {["All", "Billing & Payments", "Technical Issue", "Account Access", "Service Inquiry", "General Inquiry", "Other"].map(c => (
                                                    <label key={c} className="flex items-center gap-2 font-semibold cursor-pointer hover:bg-gray-50 p-[2px] rounded">
                                                        <input type="checkbox" className="accent-black"
                                                            checked={pendingFilters.category === c}
                                                            onChange={() => setPendingFilters(f => ({ ...f, category: c }))} /> {c}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* STATUS */}
                                    <div className="py-3 border-t border-[#000000]">
                                        <button onClick={() => toggleSection('status')} className="flex w-full items-center justify-between py-2 text-sm font-semibold">
                                            Status <ArrowRightIcon className={`transition-transform duration-200 ${openSections.status ? "rotate-90" : "rotate-0"}`} />
                                        </button>
                                        {openSections.status && (
                                            <div className="grid grid-cols-2 gap-2">
                                                {[
                                                    { label: "All", base: "bg-gray-100 text-gray-600", border: "border-gray-600" },
                                                    { label: "Received", base: "bg-green-100 text-green-700", border: "border-green-700" },
                                                    { label: "In Review", base: "bg-yellow-100 text-yellow-700", border: "border-yellow-700" },
                                                    { label: "Resolved", base: "bg-gray-100 text-gray-600", border: "border-gray-600" },
                                                ].map(a => {
                                                    const isActive = pendingFilters.status === a.label;
                                                    return (
                                                        <button key={a.label}
                                                            onClick={() => setPendingFilters(f => ({ ...f, status: a.label }))}
                                                            className={`py-[6px] text-sm font-medium rounded-full border transition ${a.base} ${isActive ? a.border : "border-transparent"}`}>
                                                            {a.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    {/* ACTIONS */}
                                    <div className="flex items-center justify-between pt-2 gap-4">
                                        <button onClick={resetAll} className="rounded-md justify-center px-4 py-1.5 text-sm flex flex-1 border border-[#000000]">Reset All</button>
                                        <button onClick={handleApplyFilters} className="rounded-md bg-black px-4 py-1.5 text-sm text-white flex flex-1 items-center justify-center gap-1">Apply</button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* SORT */}
                        <div ref={sortRef} className="relative pr-4">
                            <button onClick={() => setShowSort(p => !p)} className="flex items-center gap-1 text-nowrap">
                                <SortIcon /> Sort <ArrowRightIcon className="rotate-90" />
                            </button>
                            {showSort && (
                                <div className="absolute right-0 top-7 w-[200px] rounded-xl border bg-white shadow-lg z-30">
                                    {["A to Z", "Z to A", "Newest"].map(s => (
                                        <div key={s} onClick={() => handleSortSelect(s)}
                                            className={`px-4 py-2 text-sm hover:bg-[#F5F6F8] cursor-pointer ${(s === "A to Z" && sortDir === "asc") || (s === "Z to A" && sortDir === "desc") || (s === "Newest" && sortBy === "createdat") ? "bg-[#F5F6F8] font-semibold" : ""}`}>
                                            {s}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* TABLE */}
                <div className="flex-1 mt-4 mb-2 rounded-md overflow-hidden min-h-0 flex flex-col">
                    <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_0.7fr_0.4fr] bg-[rgba(0,0,0,0.96)] text-[rgba(255,255,255,1)] px-4 py-3 text-xs font-semibold">
                        <span>Ticket ID</span><span>User Name</span><span>Category</span>
                        <span>Created time</span><span>Last updated time</span><span>Status</span>
                        <span className="text-right">Action</span>
                    </div>
                    <div className="flex-1 overflow-y-auto custom-scrollbar-css">
                        {tableLoading ? (
                            <div className="flex justify-center p-8"><RingLoaderIcon size={4} /></div>
                        ) : tableData.length ? (
                            tableData.map((item: any, i: number) => {
                                const row = getTableRowData(item);
                                const active = selected === row.id;
                                return (
                                    <div key={row.id || i} className={`grid grid-cols-[1fr_1fr_1fr_1fr_1fr_0.7fr_0.4fr] px-4 py-2 text-sm cursor-pointer border-b ${i % 2 ? "bg-[#eeecec]" : "bg-white"}`}>
                                        <span className="pr-2 py-1 self-center">{row.ticketId}</span>
                                        <span className="pr-2 py-1 self-center">{row.userName}</span>
                                        <span className="pr-2 py-1 self-center">{row.category}</span>
                                        <span className="pr-2 py-1 self-center">{row.createdTime}</span>
                                        <span className="pr-2 py-1 self-center">{row.updatedTime}</span>
                                        <span className="pr-2 py-1 self-center">
                                            <span className={`px-3 py-2 rounded-full text-xs font-medium ${row.status === "Resolved" ? "bg-gray-100 text-gray-600" : row.status === "Received" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                                                {row.status}
                                            </span>
                                        </span>
                                        <span className={`self-center px-1 cursor-pointer flex whitespace-nowrap font-roboto text-right font-semibold text-[12px] leading-[12px] tracking-[0.2px] ${active ? "text-[#616161]/80" : "text-[#000000]/80 underline"}`}
                                            onClick={() => { openManageTicketModal("viewticket", item); }}>
                                            {"Manage"}
                                            {/* <span className='size-3'>{row.status === "Resolved" && <MessageDotIcon size={20} />}</span> */}
                                        </span>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="p-8 text-center text-gray-400 text-sm">No tickets found</div>
                        )}
                    </div>
                </div>

                {/* FOOTER */}
                <div className="mt-auto flex items-center justify-between border rounded-md border-[#E5E7EB] bg-white px-2 py-3 text-xs">
                    <span>{totalResults > 0 ? `${startItem}-${endItem} of ${totalResults} items` : "No items"}</span>

                    <div className="flex items-center gap-3">
                        {totalPages > 1 && (
                            <div className="flex items-center gap-2 pr-3">
                                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0}
                                    className={`px-3 py-1 rotate-180 rounded border ${currentPage === 0 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-black border-[#E5E7EB] hover:bg-gray-100'}`}>
                                    <ArrowRightIcon />
                                </button>
                                <span className="px-2 text-xs font-medium">Page {currentPage + 1} of {totalPages}</span>
                                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1}
                                    className={`px-3 py-1 rounded border ${currentPage >= totalPages - 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-black border-[#E5E7EB] hover:bg-gray-100'}`}>
                                    <ArrowRightIcon />
                                </button>
                            </div>
                        )}
                    </div>

                    <div ref={itemsperpageRef} className="relative pr-4">
                        <button onClick={() => setShowItemPerPage(p => !p)} className="flex items-center gap-1 text-nowrap">
                            Items per page: <span>{itemsPerPage}</span> <ArrowRightIcon className="rotate-90" />
                        </button>
                        {showItemPerPage && (
                            <div className="absolute right-0 bottom-7 w-[200px] rounded-xl border bg-white shadow-lg z-30">
                                {[10, 50, 100, 200].map(item => (
                                    <div key={item} onClick={() => handleItemsPerPageChange(item as itemPerPageTY)}
                                        className={`px-4 py-2 text-sm hover:bg-[#F5F6F8] cursor-pointer ${item === itemsPerPage ? "bg-[#F5F6F8]" : ""}`}>
                                        {item + " - Items per page"}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default memo(SupportTrack);